import { useI18n } from "@/contexts/i18nContext";
import { SearchFilterV4 } from "@/types/SearchFilter";
import { formatDateForApi } from "@/utils";
import {
  getDateRange,
  getMentionViewDateRanges,
} from "@/views/MentionsView/MentionLayout/ranges";
import DialogCustomDates from "@components/SearchDates/partials/DialogCustomDates";
import { Button } from "@components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { CalendarPlus2Icon, CalendarRangeIcon } from "lucide-react";
import { useState } from "react";

export default function SearchDates({
  selectedStartDate,
  searchStartDate,
  selectedEndDate,
  minStartDate,
  maxEndDate,
  onChange,
}: {
  onChange: (value: SearchFilterV4, clear?: boolean) => void;
  selectedStartDate: SearchFilterV4["start_date"];
  selectedEndDate: SearchFilterV4["end_date"];
  minStartDate: string | undefined;
  maxEndDate: string | undefined;
  searchStartDate?: string;
}) {
  const { formatDateRange } = useI18n();

  // Local state
  const [isCustomDatesOpen, setIsCustomDatesOpen] = useState<boolean>(false);

  // Helpers
  const dateRanges = getMentionViewDateRanges(searchStartDate);

  const hasDateRange = selectedStartDate && selectedEndDate;
  const defaultRange = dateRanges.find((r) => r.default);
  const selectedRange = hasDateRange
    ? getDateRange(selectedStartDate, selectedEndDate, dateRanges)
    : defaultRange;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button data-testid="datepicker--trigger" variant="outline" />
          }
        >
          <CalendarRangeIcon />
          {selectedRange ? (
            i18n._(selectedRange.label)
          ) : hasDateRange ? (
            formatDateRange(selectedStartDate, selectedEndDate, "PP")
          ) : (
            <Trans>Välj datum</Trans>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-72" align="end">
          <DropdownMenuRadioGroup
            value={selectedRange?.identifier}
            onValueChange={(newIdentifier) => {
              const newRange = dateRanges.find(
                (r) => r.identifier === newIdentifier
              );

              if (!newRange) {
                return;
              }

              onChange({
                start_date: formatDateForApi(newRange.startDate),
                end_date: formatDateForApi(newRange.endDate),
              });
            }}
          >
            {dateRanges.map(({ label, identifier }) => {
              return (
                <DropdownMenuRadioItem
                  data-testid={`datepicker--preset-${i18n._(label)}`}
                  value={identifier}
                  key={identifier}
                >
                  {i18n._(label)}
                </DropdownMenuRadioItem>
              );
            })}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              setIsCustomDatesOpen(true);
            }}
          >
            <CalendarPlus2Icon />
            <Trans>Välj datumspann…</Trans>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DialogCustomDates
        onOpenChange={setIsCustomDatesOpen}
        startDate={selectedStartDate}
        minStartDate={minStartDate}
        maxEndDate={maxEndDate}
        endDate={selectedEndDate}
        open={isCustomDatesOpen}
        onChange={onChange}
      />
    </>
  );
}
